import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { getPageOfChromeDevices, UserKeys } from '../../../api/users-api';
import {
  ActiveTimeRangesModel,
  ChromeDeviceDate,
  ChromeDeviceModel,
  CpuInfo,
  Page,
  RecentUsersModel,
} from '../../../../types';
import { useMemo, useState } from 'react';
import ChromeDeviceDialog from './device-details-dialog';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import { useAnalytics } from '../../../shared/analytics/create-events';
import EnableDeviceDialog from './enable-device-dialog';
import DisableDeviceDialog from './disable-device-dialog';
import DeprovisionDeviceDialog from './deprovision-device-dialog';
import {
  GridColDef,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridFilterModel,
  GridSortModel,
  GridInitialState,
} from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import ChromeDevicesMoveDialog from './chrome-devices-move-dialog';
import AvatarChip from '../../../shared/avatar-chip';
import { Messages } from '../../../utils/message-constants';
import ChromeDevicesPaginatedGridToolbar from './chrome-devices-paginated-grid-toolbar';
import { createGridOverlays } from '../../grid-overlay/no-data-overlay-helpers';
import { Gauge } from '@mui/x-charts/Gauge';
import { useUserSettings } from '../../../utils/use-user-settings';
import useGridColumns from '../../../shared/hooks/use-grid-columns-latest';
import SharedDataGridServer from '../../../shared/shared-data-grid-server';
import { useToolbar } from '../../../shared/shared-data-grid-toolbar-helper';
import StatusLabel, { StatusType } from '../../../shared/status-label';
import LaptopChromebook from '@mui/icons-material/LaptopChromebook';

interface ChromeDevicesPaginatedGridProps {
  changeLogRoles: string[];
  toolsRoles: string[];
}

export default function ChromeDevicesPaginatedGridOld({
  changeLogRoles,
  toolsRoles,
}: ChromeDevicesPaginatedGridProps) {
  const { createEvent } = useAnalytics();
  const [selectedDevice, setSelectedDevice] = useState<ChromeDeviceModel>();
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [openViewDeviceDetails, setOpenViewDeviceDetails] = useState(false);
  const [openEnableDevices, setOpenEnableDevices] = useState(false);
  const [openDisableDevices, setOpenDisableDevices] = useState(false);
  const [openDeprovisionDevices, setOpenDeprovisionDevices] = useState(false);
  const [openMoveOrgUnitDialog, setOpenMoveOrgUnitDialog] = useState(false);

  const columns: GridColDef<ChromeDeviceModel>[] = [
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      pinnable: false,
      flex: 2,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'contains')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      valueOptions: [
        { label: 'active', value: 'ACTIVE' },
        { label: 'disabled', value: 'DISABLED' },
        { label: 'deprovisioned', value: 'DEPROVISIONED' },
      ],
      pinnable: false,
      width: 140,
      filterOperators: getGridSingleSelectOperators()
        .filter((op) => op.value === 'is')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      renderCell: (params) => {
        const status = (params.value?.toUpperCase() as StatusType) ?? null;
        return status ? <StatusLabel status={status} /> : null;
      },
    },
    {
      field: 'annotatedAssetId',
      headerName: 'Asset ID',
      pinnable: false,
      flex: 1,
      sortable: false,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'contains')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
    },
    {
      field: 'orgUnitPath',
      headerName: 'Organizational Unit',
      flex: 1.5,
      pinnable: false,
      filterable: false,
      sortable: false,
      valueGetter: (_value, row) => {
        if (row.orgUnitPath === null) return '';
        if (row.orgUnitPath === '/') return primaryDomain;
        return row.orgUnitPath?.split('/').slice(-1)[0];
      },
    },
    {
      field: 'lastEnrollmentTime',
      headerName: 'Enrollment Time',
      pinnable: false,
      flex: 1,
      type: 'date',
      filterable: false,
      sortable: false,
      valueGetter: (value: ChromeDeviceDate) => {
        if (value === null) return 'N/A';
        const date = new Date(value.value);
        return date;
      },
      valueFormatter: (params: Date) => {
        const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
        return formattedDate;
      },
    },
    {
      field: 'lastSync',
      headerName: 'Last Policy Sync',
      pinnable: false,
      flex: 1,
      type: 'date',
      filterable: false,
      valueGetter: (value: ChromeDeviceDate) => {
        if (value === null) return 'N/A';
        const date = new Date(value.value);
        return date;
      },
      valueFormatter: (params: Date) => {
        const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
        return formattedDate;
      },
    },
    {
      field: 'annotatedLocation',
      headerName: 'Location',
      pinnable: false,
      flex: 1,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
    },
    {
      field: 'supportEndDate',
      headerName: 'End of Support',
      pinnable: false,
      flex: 1,
      type: 'date',
      filterable: false,
      sortable: false,
      valueGetter: (value: number) => {
        if (value === null) return 'N/A';
        if (value === 0) return 'N/A';
        const date = new Date(value);
        return date;
      },
      valueFormatter: (params: Date | string) => {
        if (params === 'N/A') return 'N/A';
        const formattedDate = dayjs(params).format('MMM YYYY');
        return formattedDate;
      },
    },
    {
      field: 'usedDiskSpace',
      headerName: 'Used Disk Space',
      pinnable: false,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        if (
          !params ||
          !params.row ||
          !params.row.diskSpaceUsage ||
          !params.row.diskSpaceUsage.usedBytes ||
          params.row.diskSpaceUsage.usedBytes === ''
        )
          return 'Unknown';
        const usedBytes = parseInt(params.row.diskSpaceUsage.usedBytes, 10);
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(usedBytes) / Math.log(1024));
        if (i === 0) return `${usedBytes} ${sizes[i]}`;
        return `${(usedBytes / Math.pow(1024, i)).toFixed(0)} ${sizes[i]}`;
      },
    },
    {
      field: 'totalDiskSpace',
      headerName: 'Total Disk Space',
      pinnable: false,
      flex: 1,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        if (
          !params ||
          !params.row ||
          !params.row.diskSpaceUsage ||
          !params.row.diskSpaceUsage.capacityBytes ||
          params.row.diskSpaceUsage.capacityBytes === ''
        )
          return 'Unknown';
        const capacityBytes = parseInt(
          params.row.diskSpaceUsage.capacityBytes,
          10
        );
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(capacityBytes) / Math.log(1024));
        if (i === 0) return `${capacityBytes} ${sizes[i]}`;
        return `${(capacityBytes / Math.pow(1024, i)).toFixed(0)} ${sizes[i]}`;
      },
    },
    {
      field: 'usage',
      headerName: 'Storage Used',
      pinnable: false,
      filterable: false,
      sortable: false,
      display: 'flex',
      width: 130,
      renderCell: (params) => {
        if (
          !params ||
          !params.row ||
          !params.row.diskSpaceUsage ||
          !params.row.diskSpaceUsage.usedBytes ||
          params.row.diskSpaceUsage.usedBytes === '' ||
          !params.row.diskSpaceUsage.capacityBytes ||
          params.row.diskSpaceUsage.capacityBytes === ''
        )
          return 'Unknown';
        const usedBytes = parseInt(params.row.diskSpaceUsage.usedBytes, 10);
        const capacityBytes = parseInt(
          params.row.diskSpaceUsage.capacityBytes,
          10
        );
        return (
          <Gauge
            width={60}
            height={60}
            value={Math.round((usedBytes / capacityBytes) * 100)}
            text={({ value }) => (value ? value + `%` : '0%')}
          />
        );
      },
    },
    {
      field: 'notes',
      headerName: 'Notes',
      pinnable: false,
      flex: 1,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
    },
    {
      field: 'autoUpdateExpiration',
      headerName: 'Automatic Updates Until',
      pinnable: false,
      type: 'date',
      filterable: false,
      sortable: false,
      valueGetter: (value: number) => {
        if (value === null) return 'N/A';
        const date = new Date(value);
        return date;
      },
      valueFormatter: (params: Date) => {
        const formattedDate = dayjs(params).format('MMM YYYY');
        return formattedDate;
      },
    },
    {
      field: 'annotatedUser',
      headerName: 'User',
      pinnable: false,
      flex: 1.5,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      renderCell: (params) => {
        if (!params || params.value === '') return '';
        return <AvatarChip email={params.value} type="User" />;
      },
    },
    {
      field: 'recentUsers',
      headerName: 'Most Recent User',
      pinnable: false,
      flex: 1.5,
      sortable: false,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'equals')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      valueGetter: (value: RecentUsersModel[]) => {
        if (value === null) return '';
        return value[0].email;
      },
      renderCell: (params) => {
        if (!params || params.value === '') return '';
        return <AvatarChip email={params.value} type="User" />;
      },
    },
    {
      field: 'model',
      headerName: 'Model',
      pinnable: false,
      flex: 1.5,
      sortable: false,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'contains')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
    },
    {
      field: 'osVersion',
      headerName: 'ChromeOS Version',
      pinnable: false,
      flex: 1,
      sortable: false,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'contains')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
    },
    {
      field: 'meid',
      headerName: 'MEID / IMEI',
      pinnable: false,
      width: 100,
      sortable: false,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'contains')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
    },
    {
      field: 'activeTimeRanges',
      headerName: 'Last User Activity',
      pinnable: false,
      flex: 1,
      type: 'date',
      sortable: false,
      filterable: false,
      valueGetter: (value: ActiveTimeRangesModel[] | null) => {
        if (value === null) return '';
        return new Date(value[0].date.value);
      },
      valueFormatter: (params: Date | string) => {
        if (params === '') return 'Never';
        const formattedDate = dayjs(params).format('MMM D, YYYY, h:mm A');
        return formattedDate;
      },
    },
    {
      field: 'cpuInfo',
      headerName: 'CPU Model Name',
      pinnable: false,
      flex: 1,
      sortable: false,
      filterOperators: getGridStringOperators()
        .filter((op) => op.value === 'contains')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      valueGetter: (value?: CpuInfo[]) => {
        if (value === null) return '';
        return value?.[0].model;
      },
    },
    {
      field: 'systemRamTotal',
      headerName: 'Total RAM',
      pinnable: false,
      flex: 1,
      type: 'number',
      filterable: false,
      sortable: false,
      valueFormatter: (params: number) => {
        const bytes = params;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        if (bytes === 0) return '';
        const i = Math.floor(Math.log(bytes) / Math.log(1024));
        if (i === 0) return `${bytes} ${sizes[i]}`;
        return `${(bytes / Math.pow(1024, i)).toFixed(0)} ${sizes[i]}`;
      },
    },
    {
      field: 'bootMode',
      headerName: 'Boot mode',
      pinnable: false,
      flex: 1,
      type: 'singleSelect',
      sortable: false,
      valueOptions: [
        { label: 'Verified', value: 'Verified' },
        { label: 'Developer', value: 'Dev' },
        { label: 'Unknown', value: '' },
      ],
      filterOperators: getGridSingleSelectOperators()
        .filter((op) => op.value === 'is')
        .map((operator) => {
          return {
            ...operator,
          };
        }),
      valueFormatter: (params: string | null) => {
        if (params === '' || !params) return 'Unknown';
        if (params === 'Verified') return 'Verified';
        if (params === 'Dev') return 'Developer';
      },
    },
  ];

  const { initialState, computedColumns } = useGridColumns(
    columns,
    'chromeDevicesGridStates'
  );
  const defaultGridState: GridInitialState = {
    columns: {
      columnVisibilityModel: {
        notes: false,
        annotatedLocation: false,
        recentUsers: false,
        model: false,
        osVersion: false,
        meid: false,
        cpuInfo: false,
        systemRamTotal: false,
        bootMode: false,
        activeTimeRanges: false,
        supportEndDate: false,
        usedDiskSpace: false,
        totalDiskSpace: false,
      },
    },
  };

  function initialStateSortCheck(gridState: GridInitialState | undefined): {
    filter: string;
    sortColumn: string;
    sortDirection: string;
  } {
    let filter = '';
    let sortColumn = '';
    let sortDirection = '';

    const filterItem = gridState?.filter?.filterModel?.items?.[0];

    if (filterItem && filterItem.value) {
      const filterFieldMapping: Record<string, string> = {
        status: 'status',
        annotatedAssetId: 'asset_id',
        serialNumber: 'id',
        annotatedLocation: 'location',
        annotatedUser: 'user',
        recentUsers: 'recent_user',
        notes: 'note',
        model: 'public_model_name',
        osVersion: 'chrome_version',
        cpuInfo: 'cpu_model_name',
        bootMode: 'boot_mode',
      };
      const apiField = filterFieldMapping[filterItem.field] ?? filterItem.field;
      filter = `${apiField}:${filterItem.value}`;
    }

    const sortItem = gridState?.sorting?.sortModel?.[0];

    if (sortItem) {
      sortDirection = sortItem.sort === 'asc' ? 'ASCENDING' : 'DESCENDING';

      const sortFieldMapping: Record<string, string> = {
        serialNumber: 'SERIAL_NUMBER',
        status: 'STATUS',
        lastSync: 'LAST_SYNC',
        annotatedLocation: 'ANNOTATED_LOCATION',
        notes: 'NOTES',
        annotatedUser: 'ANNOTATED_USER',
      };
      sortColumn = sortFieldMapping[sortItem.field] ?? '';
    }

    return { filter, sortColumn, sortDirection };
  }

  const [paginationModel, setPaginationModel] = useState<{
    pageSize: number;
    page: number;
    cursor: string | null;
    filter: string;
    sortColumn: string;
    sortDirection: string;
  }>(() => {
    const { filter, sortColumn, sortDirection } =
      initialStateSortCheck(initialState);
    return {
      pageSize: initialState?.pagination?.paginationModel?.pageSize || 50,
      page: 0,
      cursor: null,
      filter,
      sortColumn,
      sortDirection,
    };
  });

  const { primaryDomain, viewType } = useUserSettings();

  function onFilterChange(newModel: GridFilterModel) {
    if (newModel.items.length > 0 && newModel.items[0].value) {
      if (newModel.items[0].field === 'status') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `status:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'annotatedAssetId') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `asset_id:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'serialNumber') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `id:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'annotatedLocation') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `location:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'annotatedUser') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `user:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'recentUsers') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `recent_user:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'notes') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `note:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'model') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `public_model_name:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'osVersion') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `chrome_version:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'cpuInfo') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `cpu_model_name:${newModel.items[0].value}`,
        });
      } else if (newModel.items[0].field === 'bootMode') {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `boot_mode:${newModel.items[0].value}`,
        });
      } else {
        setPaginationModel({
          ...paginationModel,
          page: 0,
          cursor: null,
          filter: `${newModel.items[0].field}:${newModel.items[0].value}`,
        });
      }
    } else {
      setPaginationModel({
        ...paginationModel,
        page: 0,
        cursor: null,
        filter: '',
      });
    }
  }

  function onSortChange(newModel: GridSortModel) {
    if (newModel.length > 0) {
      const updatedSortDirection =
        newModel[0].sort === 'asc' ? 'ASCENDING' : 'DESCENDING';
      if (newModel[0].field === 'serialNumber') {
        setPaginationModel({
          ...paginationModel,
          sortColumn: 'SERIAL_NUMBER',
          sortDirection: updatedSortDirection,
        });
      } else if (newModel[0].field === 'status') {
        setPaginationModel({
          ...paginationModel,
          sortColumn: 'STATUS',
          sortDirection: updatedSortDirection,
        });
      } else if (newModel[0].field === 'lastSync') {
        setPaginationModel({
          ...paginationModel,
          sortColumn: 'LAST_SYNC',
          sortDirection: updatedSortDirection,
        });
      } else if (newModel[0].field === 'annotatedLocation') {
        setPaginationModel({
          ...paginationModel,
          sortColumn: 'ANNOTATED_LOCATION',
          sortDirection: updatedSortDirection,
        });
      } else if (newModel[0].field === 'notes') {
        setPaginationModel({
          ...paginationModel,
          sortColumn: 'NOTES',
          sortDirection: updatedSortDirection,
        });
      } else if (newModel[0].field === 'annotatedUser') {
        setPaginationModel({
          ...paginationModel,
          sortColumn: 'ANNOTATED_USER',
          sortDirection: updatedSortDirection,
        });
      }
    } else {
      setPaginationModel({
        ...paginationModel,
        sortColumn: '',
        sortDirection: '',
      });
    }
  }

  const {
    data: chromeDevicesPage,
    isLoading: chromeDevicesLoading,
    isFetching: chromeDevicesFetching,
  } = useQuery({
    queryKey: [
      UserKeys.ChromeDevicesPage,
      paginationModel.page,
      paginationModel.pageSize,
      paginationModel.filter,
      paginationModel.sortColumn,
      paginationModel.sortDirection,
    ],
    queryFn: () => {
      const object: Page<ChromeDeviceModel[]> = {
        results: [],
        firstPull: paginationModel.page === 0,
        searchTime: 0,
        pageSize: paginationModel.pageSize,
        cursor: paginationModel.cursor,
        nameSpace: '',
      };
      return getPageOfChromeDevices(
        object,
        paginationModel.filter,
        paginationModel.sortColumn,
        paginationModel.sortDirection
      );
    },
    placeholderData: keepPreviousData,
    meta: {
      errorMessage: Messages.Errors.Network.Loading('chrome devices'),
    },
  });

  const selectedDevices =
    chromeDevicesPage?.results?.filter((device) =>
      selectedRows.includes(device.deviceId)
    ) || [];

  const toolbarProps = useMemo(
    () => ({
      selectedDevices,
      toolsRoles,
      setOpenDisableDevices,
      setOpenEnableDevices,
      setOpenDeprovisionDevices,
      setOpenMoveOrgUnitDialog,
    }),
    [selectedDevices, toolsRoles]
  );

  const CustomToolbar = useToolbar(
    ChromeDevicesPaginatedGridToolbar,
    toolbarProps
  );
  return (
    <>
      <SharedDataGridServer
        gridKey={viewType.chromeDevicesGridStates?.activeGridState || 'default'}
        slots={{
          toolbar: CustomToolbar,
          ...createGridOverlays('Chrome Devices', <LaptopChromebook />),
        }}
        checkboxSelection
        disableRowSelectionOnClick
        columns={computedColumns}
        getRowId={(row) => row.deviceId}
        initialState={initialState || defaultGridState}
        data={chromeDevicesPage}
        onRowClick={(params) => {
          setSelectedDevice(params.row);
          setOpenViewDeviceDetails(true);
          createEvent(analytics.devices.device_management.viewed_device, {
            module: ModuleList.modules.devices.name,
            section: ModuleList.modules.devices.sections.chromeDevices,
          });
        }}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'pointer',
          },
        }}
        paginationModel={paginationModel}
        onPaginationModelChange={(newModel) => {
          if (chromeDevicesLoading) return;

          if (newModel.pageSize !== paginationModel.pageSize) {
            setPaginationModel({
              page: 0,
              pageSize: newModel.pageSize,
              cursor: null,
              filter: paginationModel.filter,
              sortColumn: paginationModel.sortColumn,
              sortDirection: paginationModel.sortDirection,
            });
          } else {
            setPaginationModel({
              ...newModel,
              cursor: chromeDevicesPage?.cursor || '',
              filter: paginationModel.filter,
              sortColumn: paginationModel.sortColumn,
              sortDirection: paginationModel.sortDirection,
            });
          }
        }}
        onFilterModelChange={(newModel) => {
          if (chromeDevicesLoading) return;
          onFilterChange(newModel);
        }}
        onSortModelChange={(newModel) => {
          if (chromeDevicesLoading) return;
          onSortChange(newModel);
        }}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        loading={chromeDevicesLoading || chromeDevicesFetching}
      />
      {selectedDevice && openViewDeviceDetails ? (
        <ChromeDeviceDialog
          open={openViewDeviceDetails}
          handleClose={() => setOpenViewDeviceDetails(false)}
          selectedDevice={selectedDevice}
        />
      ) : null}
      {selectedRows.length > 0 && openEnableDevices ? (
        <EnableDeviceDialog
          open={openEnableDevices}
          handleClose={() => setOpenEnableDevices(false)}
          selectedDevices={selectedDevices}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedRows.length > 0 && openDisableDevices ? (
        <DisableDeviceDialog
          open={openDisableDevices}
          handleClose={() => setOpenDisableDevices(false)}
          selectedDevices={selectedDevices}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedRows.length > 0 && openDeprovisionDevices ? (
        <DeprovisionDeviceDialog
          open={openDeprovisionDevices}
          handleClose={() => setOpenDeprovisionDevices(false)}
          selectedDevices={selectedDevices}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedRows.length > 0 && openMoveOrgUnitDialog ? (
        <ChromeDevicesMoveDialog
          open={openMoveOrgUnitDialog}
          handleClose={() => setOpenMoveOrgUnitDialog(false)}
          selectedDevices={selectedDevices}
          setSelectedRows={setSelectedRows}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
    </>
  );
}
